// src/Admin/AdminPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../config/firebase-config';
import { 
  getAdminSettings, 
  updateAdminSettings, 
  subscribeToAdminSettings,
  isUserAdmin 
} from '../services/adminSettings';
import './AdminPage.css';

const AdminPage = () => {
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Check if user is admin
  useEffect(() => {
    if (loading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }

    const adminStatus = isUserAdmin(user);
    setIsAdmin(adminStatus);
    setCheckingAdmin(false);

    if (!adminStatus) {
      setMessage({ type: 'error', text: 'Access denied. Only nuralhuda.co@gmail.com has admin access.' });
      setTimeout(() => navigate('/dashboard'), 2000);
    }
  }, [user, loading, navigate]);

  // Load and subscribe to settings
  useEffect(() => {
    if (!isAdmin) return;

    // Load initial settings
    const loadSettings = async () => {
      const initialSettings = await getAdminSettings();
      setSettings(initialSettings);
    };

    loadSettings();

    // Subscribe to real-time updates
    const unsubscribe = subscribeToAdminSettings((updatedSettings) => {
      setSettings(updatedSettings);
    });

    return () => unsubscribe();
  }, [isAdmin]);

  const handleToggleProtectedRoute = async () => {
    if (!settings) return;
    
    setSaving(true);
    try {
      await updateAdminSettings({
        protectedRouteEnabled: !settings.protectedRouteEnabled,
      });
      setMessage({ type: 'success', text: 'Protected Route setting updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update setting. Please try again.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handlePromptLimitChange = async (accountType, value) => {
    if (!settings) return;
    
    const numValue = value === '' || value === 'Infinity' ? Infinity : parseInt(value, 10);
    if (isNaN(numValue) && numValue !== Infinity) {
      setMessage({ type: 'error', text: 'Please enter a valid number or "Infinity"' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    setSaving(true);
    try {
      const updatedLimits = {
        ...settings.promptLimits,
        [accountType]: numValue,
      };
      await updateAdminSettings({ promptLimits: updatedLimits });
      setMessage({ type: 'success', text: 'Prompt limits updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update prompt limits. Please try again.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading || checkingAdmin) {
    return (
      <div className="admin-page">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-page">
        <div className="admin-error">{message.text || 'Access denied'}</div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="admin-page">
        <div className="admin-loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-header">
          <h1>Admin Settings</h1>
          <button 
            className="admin-back-button" 
            onClick={() => navigate('/dashboard')}
          >
            ← Back to Dashboard
          </button>
        </div>

        {message.text && (
          <div className={`admin-message admin-message-${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="admin-section">
          <h2>Authentication Settings</h2>
          <div className="admin-setting">
            <div className="admin-setting-info">
              <label className="admin-setting-label">Protected Route</label>
              <p className="admin-setting-description">
                When enabled, users must be logged in to access protected routes. 
                Disable this for conferences/demos.
              </p>
            </div>
            <div className="admin-toggle-container">
              <button
                className={`admin-toggle ${settings.protectedRouteEnabled ? 'active' : ''}`}
                onClick={handleToggleProtectedRoute}
                disabled={saving}
              >
                <span className="admin-toggle-slider"></span>
              </button>
              <span className="admin-toggle-label">
                {settings.protectedRouteEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>

        <div className="admin-section">
          <h2>Prompt Limits</h2>
          <p className="admin-section-description">
            Set daily prompt limits for each account type. Use "Infinity" for unlimited.
          </p>
          <div className="admin-prompt-limits">
            {Object.entries(settings.promptLimits || {}).map(([accountType, limit]) => (
              <div key={accountType} className="admin-prompt-limit-item">
                <label className="admin-prompt-limit-label">
                  {accountType.charAt(0).toUpperCase() + accountType.slice(1)}
                </label>
                <input
                  type="text"
                  className="admin-prompt-limit-input"
                  value={limit === Infinity ? 'Infinity' : limit}
                  onChange={(e) => handlePromptLimitChange(accountType, e.target.value)}
                  disabled={saving}
                  placeholder="Enter limit"
                />
              </div>
            ))}
          </div>
        </div>

        {settings.lastUpdated && (
          <div className="admin-footer">
            <p className="admin-last-updated">
              Last updated: {
                settings.lastUpdated.seconds 
                  ? new Date(settings.lastUpdated.seconds * 1000).toLocaleString()
                  : settings.lastUpdated.toDate 
                    ? settings.lastUpdated.toDate().toLocaleString()
                    : 'Just now'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;

